import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, SimpleTable, useToast } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { createShippingMethod, deleteShippingMethod, fetchShippingMethods, updateShippingMethod, } from '../../../api/ShippingMethodAdminApi';
import { createShippingMethodColumns } from './tableColumns';
import styles from './ShippingMethods.module.scss';
const DEFAULT_FILTERS = {
    is_free: '',
};
const toPositiveInt = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
};
const toInputValue = (value) => {
    if (value === null || value === undefined)
        return '';
    if (typeof value === 'number')
        return Number.isFinite(value) ? String(value) : '';
    return String(value).trim();
};
const toPriceValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};
const toBooleanValue = (value) => {
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return value === 1;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', '1', 'yes', 'y'].includes(normalized))
            return true;
        if (['false', '0', 'no', 'n', ''].includes(normalized))
            return false;
    }
    return false;
};
const getShippingMethodErrorMessage = (error) => {
    const payload = error?.response?.body;
    const nonFieldMessage = payload?.non_field_errors?.[0] ?? payload?.detail;
    if (typeof nonFieldMessage === 'string' && nonFieldMessage.trim()) {
        return nonFieldMessage;
    }
    const fieldMessage = payload?.name?.[0] ??
        payload?.price?.[0] ??
        payload?.is_free?.[0] ??
        payload?.estimated_days?.[0];
    if (typeof fieldMessage === 'string' && fieldMessage.trim()) {
        return fieldMessage;
    }
    return 'Failed to save shipping method';
};
const ShippingMethods = () => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [methods, setMethods] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [isRunningBulkAction, setIsRunningBulkAction] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editingMethod, setEditingMethod] = useState(null);
    const [editName, setEditName] = useState('');
    const [editPrice, setEditPrice] = useState('0');
    const [editEstimatedDays, setEditEstimatedDays] = useState('1');
    const [editIsFree, setEditIsFree] = useState(false);
    const [editIsDefault, setEditIsDefault] = useState(false);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [addName, setAddName] = useState('');
    const [addPrice, setAddPrice] = useState('0');
    const [addEstimatedDays, setAddEstimatedDays] = useState('1');
    const [addIsFree, setAddIsFree] = useState(false);
    const [addIsDefault, setAddIsDefault] = useState(false);
    const [isSavingAdd, setIsSavingAdd] = useState(false);
    const [addErrorMessage, setAddErrorMessage] = useState('');
    const latestRequestIdRef = useRef(0);
    const loadShippingMethods = useCallback(async (search, activeFilters = DEFAULT_FILTERS) => {
        const requestId = ++latestRequestIdRef.current;
        try {
            setLoading(true);
            const response = await fetchShippingMethods({
                search: search || undefined,
                is_free: activeFilters.is_free === '' ? undefined : activeFilters.is_free === 'true',
            });
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            const data = response.body;
            const methodsArray = Array.isArray(data) ? data : data.results || [];
            const transformedMethods = methodsArray.map((item) => ({
                id: item.id,
                name: item.name,
                price: item.price ?? 0,
                is_free: toBooleanValue(item.isFree ?? item.is_free),
                is_default: toBooleanValue(item.isDefault ?? item.is_default),
                estimated_days: item.estimatedDays ?? item.estimated_days,
                created_at: item.createdAt ?? item.created_at,
                updated_at: item.updatedAt ?? item.updated_at,
            }));
            setMethods(transformedMethods);
        }
        catch (error) {
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            console.error('Error loading shipping methods:', error);
            showToast({ message: 'Failed to load shipping methods', variant: 'error', noBorder: true });
            setMethods([]);
        }
        finally {
            if (requestId === latestRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [showToast]);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            loadShippingMethods(searchTerm, appliedFilters);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [loadShippingMethods, searchTerm, appliedFilters]);
    const handleApplyFilters = () => {
        setAppliedFilters(filters);
    };
    const handleResetFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
    };
    const runBulkUpdateIsFree = async (selectedRows, isFree) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIds = selectedRows.map((row) => row.id);
        setIsRunningBulkAction(true);
        try {
            const results = await Promise.allSettled(selectedIds.map((id) => updateShippingMethod(id, { is_free: isFree, ...(isFree ? { price: 0 } : {}) })));
            const successfulIds = [];
            const failedIds = [];
            results.forEach((result, index) => {
                const id = selectedIds[index];
                if (result.status === 'fulfilled') {
                    successfulIds.push(id);
                }
                else {
                    failedIds.push(id);
                }
            });
            if (successfulIds.length) {
                setMethods((prev) => prev.map((item) => (successfulIds.includes(item.id)
                    ? { ...item, is_free: isFree, ...(isFree ? { price: 0 } : {}) }
                    : item)));
                showToast({
                    message: `Updated ${successfulIds.length} shipping method${successfulIds.length === 1 ? '' : 's'}`,
                    variant: failedIds.length ? 'warning' : 'success',
                    noBorder: true,
                });
            }
            if (failedIds.length) {
                console.error('Error bulk updating shipping methods for IDs:', failedIds);
                showToast({
                    message: `Failed to update ${failedIds.length} shipping method${failedIds.length === 1 ? '' : 's'}`,
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
        catch (error) {
            console.error('Error running bulk update for shipping methods:', error);
            showToast({ message: 'Failed to update selected shipping methods', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleSetDefault = async (selectedRows) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        if (selectedRows.length > 1) {
            showToast({ message: 'Please select only one shipping method to set as default', variant: 'warning', noBorder: true });
            return;
        }
        const selected = selectedRows[0];
        if (selected.is_default) {
            showToast({ message: 'Selected shipping method is already default', variant: 'success', noBorder: true });
            return;
        }
        setIsRunningBulkAction(true);
        try {
            await updateShippingMethod(selected.id, { is_default: true });
            showToast({ message: 'Default shipping method updated', variant: 'success', noBorder: true });
            loadShippingMethods(searchTerm, appliedFilters);
        }
        catch (error) {
            console.error('Error setting default shipping method:', error);
            showToast({ message: 'Failed to update default shipping method', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleBulkDelete = async (selectedRows) => {
        if (!selectedRows.length || isRunningBulkAction)
            return;
        const selectedIds = selectedRows.map((row) => row.id);
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected shipping method${selectedIds.length === 1 ? '' : 's'}?`)) {
            return;
        }
        setIsRunningBulkAction(true);
        try {
            const results = await Promise.allSettled(selectedIds.map((id) => deleteShippingMethod(id)));
            const successfulIds = [];
            const failedIds = [];
            results.forEach((result, index) => {
                const id = selectedIds[index];
                if (result.status === 'fulfilled') {
                    successfulIds.push(id);
                }
                else {
                    failedIds.push(id);
                }
            });
            if (successfulIds.length) {
                setMethods((prev) => prev.filter((item) => !successfulIds.includes(item.id)));
                showToast({
                    message: `Deleted ${successfulIds.length} shipping method${successfulIds.length === 1 ? '' : 's'}`,
                    variant: failedIds.length ? 'warning' : 'success',
                    noBorder: true,
                });
            }
            if (failedIds.length) {
                console.error('Error deleting shipping methods for IDs:', failedIds);
                showToast({
                    message: `Failed to delete ${failedIds.length} shipping method${failedIds.length === 1 ? '' : 's'}`,
                    variant: 'error',
                    noBorder: true,
                });
            }
        }
        catch (error) {
            console.error('Error deleting selected shipping methods:', error);
            showToast({ message: 'Failed to delete selected shipping methods', variant: 'error', noBorder: true });
        }
        finally {
            setIsRunningBulkAction(false);
        }
    };
    const handleOpenEdit = (method) => {
        setEditingMethod(method);
        setEditName(method.name || '');
        setEditPrice(toInputValue(method.price));
        setEditEstimatedDays(toInputValue(method.estimated_days || 1));
        setEditIsFree(toBooleanValue(method.is_free));
        setEditIsDefault(toBooleanValue(method.is_default));
        setIsEditOpen(true);
    };
    const handleCloseEdit = () => {
        setIsEditOpen(false);
        setEditingMethod(null);
        setEditName('');
        setEditPrice('0');
        setEditEstimatedDays('1');
        setEditIsFree(false);
        setEditIsDefault(false);
    };
    const handleOpenAdd = () => {
        setAddName('');
        setAddPrice('0');
        setAddEstimatedDays('1');
        setAddIsFree(false);
        setAddIsDefault(false);
        setAddErrorMessage('');
        setIsAddOpen(true);
    };
    const handleCloseAdd = () => {
        setIsAddOpen(false);
        setAddName('');
        setAddPrice('0');
        setAddEstimatedDays('1');
        setAddIsFree(false);
        setAddIsDefault(false);
        setAddErrorMessage('');
    };
    const validatePayload = (name, price, estimatedDays, isFree) => {
        if (!name.trim()) {
            return 'Method name is required';
        }
        const parsedDays = toPositiveInt(estimatedDays);
        if (parsedDays < 1) {
            return 'Estimated days must be 1 or greater';
        }
        if (!isFree) {
            if (!price.trim()) {
                return 'Price is required for paid shipping methods';
            }
            const parsedPrice = toPriceValue(price);
            if (parsedPrice < 0) {
                return 'Price must be zero or greater';
            }
        }
        return null;
    };
    const handleSaveEdit = async () => {
        if (!editingMethod)
            return;
        const validationMessage = validatePayload(editName, editPrice, editEstimatedDays, editIsFree);
        if (validationMessage) {
            showToast({ message: validationMessage, variant: 'error', noBorder: true });
            return;
        }
        setIsSavingEdit(true);
        try {
            await updateShippingMethod(editingMethod.id, {
                name: editName.trim(),
                price: editIsFree ? 0 : toPriceValue(editPrice),
                estimated_days: toPositiveInt(editEstimatedDays),
                is_free: editIsFree,
                is_default: editIsDefault,
            });
            showToast({ message: 'Shipping method updated successfully', variant: 'success', noBorder: true });
            handleCloseEdit();
            loadShippingMethods(searchTerm, appliedFilters);
        }
        catch (error) {
            console.error('Error updating shipping method:', error);
            showToast({ message: getShippingMethodErrorMessage(error), variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingEdit(false);
        }
    };
    const handleSaveAdd = async () => {
        const validationMessage = validatePayload(addName, addPrice, addEstimatedDays, addIsFree);
        if (validationMessage) {
            setAddErrorMessage(validationMessage);
            showToast({ message: validationMessage, variant: 'error', noBorder: true });
            return;
        }
        setIsSavingAdd(true);
        setAddErrorMessage('');
        try {
            await createShippingMethod({
                name: addName.trim(),
                price: addIsFree ? 0 : toPriceValue(addPrice),
                estimated_days: toPositiveInt(addEstimatedDays),
                is_free: addIsFree,
                is_default: addIsDefault,
            });
            showToast({ message: 'Shipping method created successfully', variant: 'success', noBorder: true });
            handleCloseAdd();
            loadShippingMethods(searchTerm, appliedFilters);
        }
        catch (error) {
            console.error('Error creating shipping method:', error);
            const message = getShippingMethodErrorMessage(error);
            setAddErrorMessage(message);
            showToast({ message, variant: 'error', noBorder: true });
        }
        finally {
            setIsSavingAdd(false);
        }
    };
    const columns = createShippingMethodColumns();
    const bulkActions = [
        {
            label: 'Mark as Free',
            action: (selectedRows) => runBulkUpdateIsFree(selectedRows, true),
        },
        {
            label: 'Mark as Paid',
            action: (selectedRows) => runBulkUpdateIsFree(selectedRows, false),
        },
        {
            label: 'Set as Default',
            action: (selectedRows) => handleSetDefault(selectedRows),
        },
        {
            label: 'Delete',
            action: (selectedRows) => handleBulkDelete(selectedRows),
        },
    ];
    return (_jsxs(AdminLayout, { children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsxs("div", { className: styles.filterBar, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "shipping-method-is-free", children: "Price Type" }), _jsxs("select", { id: "shipping-method-is-free", value: filters.is_free, onChange: (event) => setFilters((prev) => ({ ...prev, is_free: event.target.value })), children: [_jsx("option", { value: "", children: "All" }), _jsx("option", { value: "true", children: "Free" }), _jsx("option", { value: "false", children: "Paid" })] })] }), _jsxs("div", { className: styles.filterActions, children: [_jsx(Button, { variant: "primaryV2", size: "small", label: "Apply Filters", onClick: handleApplyFilters }), _jsx(Button, { variant: "secondaryV2", size: "small", label: "Reset", onClick: handleResetFilters })] }), _jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by shipping method name", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) })] }), _jsx(SimpleTable, { columns: columns, data: methods, rowHeight: "60px", tableHeading: "Shipping Methods", titleStyle: {
                            color: '#000',
                            fontFamily: 'Nunito',
                            fontSize: '24px',
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '28px',
                        }, enableSelection: true, hideTopBorder: true, enablePagination: true, paginationText: "Shipping methods per page", pageSize: 20, onRowClick: (item) => handleOpenEdit(item), bulkActions: bulkActions, customButton: _jsx(Button, { variant: "primaryV2", size: "small", label: "+ Add Shipping Method", onClick: handleOpenAdd }) })] }), isEditOpen && editingMethod && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseEdit }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: "Edit Shipping Method" }) }), _jsxs("div", { className: styles.slideOutContent, children: [_jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "edit-shipping-method-name", children: "Name" }), _jsx("input", { id: "edit-shipping-method-name", value: editName, onChange: (event) => setEditName(event.target.value) })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "edit-shipping-method-price", children: "Price" }), _jsx("input", { id: "edit-shipping-method-price", type: "number", min: "0", step: "0.01", value: editIsFree ? '0' : editPrice, onChange: (event) => setEditPrice(event.target.value), disabled: editIsFree })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "edit-shipping-method-estimated-days", children: "Estimated Days" }), _jsx("input", { id: "edit-shipping-method-estimated-days", type: "number", min: "1", value: editEstimatedDays, onChange: (event) => setEditEstimatedDays(event.target.value) })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: editIsFree, onChange: (event) => setEditIsFree(event.target.checked) }), "Is Free Shipping"] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: editIsDefault, onChange: (event) => setEditIsDefault(event.target.checked) }), "Set as Default Method"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseEdit }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingEdit ? 'Saving...' : 'Save Changes', onClick: handleSaveEdit, disabled: isSavingEdit })] })] })] })), isAddOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.slideOutOverlay, onClick: handleCloseAdd }), _jsxs("div", { className: styles.slideOutPanel, children: [_jsx("div", { className: styles.slideOutHeader, children: _jsx("h2", { children: "Add Shipping Method" }) }), _jsxs("div", { className: styles.slideOutContent, children: [!!addErrorMessage && (_jsx("div", { className: styles.inlineErrorBanner, role: "alert", children: addErrorMessage })), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-shipping-method-name", children: "Name" }), _jsx("input", { id: "add-shipping-method-name", value: addName, onChange: (event) => {
                                                    setAddName(event.target.value);
                                                    if (addErrorMessage)
                                                        setAddErrorMessage('');
                                                } })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-shipping-method-price", children: "Price" }), _jsx("input", { id: "add-shipping-method-price", type: "number", min: "0", step: "0.01", value: addIsFree ? '0' : addPrice, onChange: (event) => {
                                                    setAddPrice(event.target.value);
                                                    if (addErrorMessage)
                                                        setAddErrorMessage('');
                                                }, disabled: addIsFree })] }), _jsxs("div", { className: styles.filterField, children: [_jsx("label", { htmlFor: "add-shipping-method-estimated-days", children: "Estimated Days" }), _jsx("input", { id: "add-shipping-method-estimated-days", type: "number", min: "1", value: addEstimatedDays, onChange: (event) => {
                                                    setAddEstimatedDays(event.target.value);
                                                    if (addErrorMessage)
                                                        setAddErrorMessage('');
                                                } })] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: addIsFree, onChange: (event) => setAddIsFree(event.target.checked) }), "Is Free Shipping"] }), _jsxs("label", { className: styles.checkboxField, children: [_jsx("input", { type: "checkbox", checked: addIsDefault, onChange: (event) => setAddIsDefault(event.target.checked) }), "Set as Default Method"] })] }), _jsxs("div", { className: styles.slideOutFooter, children: [_jsx(Button, { variant: "secondaryV2", size: "small", label: "Cancel", onClick: handleCloseAdd }), _jsx(Button, { variant: "primaryV2", size: "small", label: isSavingAdd ? 'Saving...' : 'Create Shipping Method', onClick: handleSaveAdd, disabled: isSavingAdd })] })] })] }))] }));
};
export default ShippingMethods;
